import { MigrationEventStepType } from "./MigrationEventStepType";
import {MigrationType} from "./MigrationType";

export interface DiscoveryCycleStatistics {
  migrationType: MigrationType;
  latest: DiscoveryCycle;
  secondLatest: DiscoveryCycle | null;
}

export interface DiscoveryCycle {
  id: string
  startDate: string;
  endDate: string;
  eventStep: MigrationEventStepType;
  clientId: string;

  // The link census the client reports when it finishes a cycle. A total is absent where the client ran
  // without a cache and held no map of the whole source to count from; `censusState` says which case applies.
  linksScanned?: number;
  linksFilesDiscovered?: number;
  linksTargetEmpty?: number;
  linksPartiallyDiscovered?: number;
  linksNothingDiscovered?: number;
  linksExcluded?: number;
  linksDuplicateTarget?: number;

  // What those links yielded, in files. A link is not a file — one folder link can stand for a whole tree — so
  // these are the only numbers here that say how much was actually found. `filesProduced` is what the links
  // yielded before anything was set aside, and the rest decompose it; blocked and unmodified files were
  // discovered, so treating them as losses understates the scan.
  filesProduced?: number;
  filesUploadable?: number;
  filesBlocked?: number;
  filesUnmodified?: number;
  filesFiltered?: number;
  filesDuplicate?: number;
  /** Files that threw while being processed. An error, not a discovery outcome. */
  filesErrored?: number;

  exceptionRowsReported: number;
  rootsUnreachable: number;

  /**
   * What accounts for the gap between the outcome buckets and the rows that reached the server — links withheld
   * beneath a root already known unreachable, and links whose report failed. Absent from a client that cannot
   * measure them, which is not the same as zero.
   */
  linksSuppressed?: number;
  linksUnreported?: number;
  /**
   * Concluded links the scan deliberately did not re-send because nothing about them had changed. The
   * largest term in a settled firm, and what makes the uploaded rows reconcilable against the buckets.
   */
  linksUnchanged?: number;
  /** Entities whose report did not land, and entities reported mid-scan. Either makes absence unreadable. */
  entitiesUnreported?: number;
  entitiesPartiallyScanned?: number;
  /** The server ended this cycle when the client opened the next one without ending it. */
  closedByServer: boolean;
  /**
   * Entities the source offered and entities the cycle scanned. Absent from a client that does not count
   * them, which is also what separates a withheld link census from one that was never reported.
   */
  entitiesInScope?: number;
  entitiesScanned?: number;
  /** A configured root could not be read, so even a complete census is not a clean account of the source. */
  degraded: boolean;

  /**
   * Why the counters above are missing or partial, if they are. Server-classified — do not re-derive it from
   * which fields are absent, because the case that matters (`withheld`) is indistinguishable from `notReported`
   * by counters alone and the two mean opposite things: a fault in one cycle versus a fact about the firm.
   */
  censusState: DiscoveryCycleCensusState;

  /**
   * The client ended the cycle before its link reports landed and drains them afterwards. Until `drainedOn` is
   * set nothing has been retired on this cycle's account; `drainComplete` false means the drain dropped an
   * entity, which blocks the sweep the way a degraded pass does.
   */
  reportsPending: boolean;
  drainedOn?: string;
  drainComplete?: boolean;

  /** How far this cycle's view of its scope can be trusted. Server-evaluated; see `DiscoveryCycleCoverage`. */
  coverage: DiscoveryCycleCoverage;
}

/**
 * How far a finished cycle's view of its scope can be trusted, which is what decides whether links nobody reported
 * are cleared out. Evaluated server-side by the same rule the clearing itself runs on, so the page and the clearing
 * cannot disagree.
 */
export enum DiscoveryCycleCoverage {
  /** Saw every entity its source offered, and nothing went wrong. */
  Complete = "complete",
  /** Something went wrong, but only where the cycle can say; clearing runs everywhere else. */
  Bounded = "bounded",
  /** Something went wrong that the cycle cannot pin down, so nothing is cleared out. */
  Unbounded = "unbounded",
  /** The client did not send the counts that would settle it. Not a fault — most of the fleet today. */
  Unproven = "unproven",
  /** Reports still landing. Settles on its own; rendered as the "Reports pending" badge rather than a verdict. */
  AwaitingDrain = "awaitingDrain",
}

/**
 * Why a cycle's census is missing or partial, if it is. Classified server-side from the stored counters so the
 * portal and the metrics cannot reach different conclusions about the same cycle.
 */
export enum DiscoveryCycleCensusState {
  /** No census, and none was ever claimed — a client predating link reporting. */
  NotReported = "notReported",
  /** The cycle never reported an end; the server closed it when the next one started. */
  ClosedByServer = "closedByServer",
  /** The client stated a mode and then withheld every counter. The work happened; the accounting was lost. */
  Withheld = "withheld",
  /** Outcome buckets present, scan never totalled — normal for a client running without a cache. */
  WithoutScanTotal = "withoutScanTotal",
  /** Scan total and buckets both present, so the census reconciles. */
  Complete = "complete",
}

/** Whether there is a census band worth rendering at all. */
export const hasCensusFigures = (cycle: DiscoveryCycle): boolean =>
  cycle.censusState === DiscoveryCycleCensusState.WithoutScanTotal ||
  cycle.censusState === DiscoveryCycleCensusState.Complete;
